import { type FC } from 'react';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { getItem } from './localStorage';
import { isRecord } from './type-guards';

/*
 * React Query Devtools configuration
 *
 * Reads once on mount — refresh the page after changing the value.
 *
 * Storage key: infield-devtools
 *
 * Full example:
 *   {"enabled": true, "position": "bottom-left"}
 *
 * Valid positions: top-left | top-right | bottom-left | bottom-right
 * Falls back to defaults (enabled: true, position: bottom-left) for missing or invalid values.
 *
 * Console helpers:
 *   localStorage.setItem('infield-devtools', JSON.stringify({ enabled: false }))
 *   localStorage.setItem('infield-devtools', JSON.stringify({ position: 'top-right' }))
 *   localStorage.removeItem('infield-devtools')
 */

const STORAGE_KEY = 'infield-devtools';

type DevtoolsPosition = Parameters<typeof ReactQueryDevtools>[0]['position'];

type DevtoolsConfig = {
  enabled: boolean;
  position: DevtoolsPosition;
};

const DEFAULT_CONFIG: DevtoolsConfig = {
  enabled: true,
  position: 'bottom',
};

const VALID_POSITIONS: DevtoolsPosition[] = ['top', 'left', 'bottom', 'right'];

const isValidPosition = (value: unknown): value is DevtoolsPosition =>
  VALID_POSITIONS.some((pos) => pos === value);

export const getConfig = (): DevtoolsConfig => {
  const raw = getItem(STORAGE_KEY);
  const stored = isRecord(raw) ? raw : {};
  return {
    enabled:
      typeof stored.enabled === 'boolean'
        ? stored.enabled
        : DEFAULT_CONFIG.enabled,
    position: isValidPosition(stored.position)
      ? stored.position
      : DEFAULT_CONFIG.position,
  };
};

const DevtoolsWrapper = () => {
  const { enabled, position } = getConfig();
  if (!enabled) return null;
  return <ReactQueryDevtools position={position} />;
};

export const ReactQueryDevtoolsWrapper: FC =
  process.env.NODE_ENV !== 'development' ? () => null : DevtoolsWrapper;
